import {computed, ref, watch} from 'vue';
import {defineStore} from 'pinia';
import {UserIdentity, UserResource, Type} from '@//common/api';
import {userEndpoints} from './user.endpoints';
import cookie from 'js-cookie';
import {$fetch} from '@malt/vue-context';
import {useLanguages} from '@/logged/common/composables/useLanguages';

export const useUser = defineStore('user', () => {
    const user = ref<UserResource>();

    // - Identities
    const userIdentities = computed<UserIdentity[]>(() => user.value?.identities ?? []);
    const selectedIdentity = computed<UserIdentity | undefined>(() => userIdentities.value.find(({id}) => user.value?.selectedIdentityId === id));
    const hasSelectedClientIdentity = computed<boolean>(() => selectedIdentity.value?.type === 'CLIENT');
    const hasSelectedFreelanceIdentity = computed<boolean>(() => selectedIdentity.value?.type === 'FREELANCE');

    const cachedIdentityRole = ref<Type>();

    watch(
        selectedIdentity,
        (newIdentity) => {
            const currentIdentityRole = sessionStorage.getItem('cachedIdentityRole');
            if (newIdentity) {
                cachedIdentityRole.value = newIdentity.type;
                sessionStorage.setItem('cachedIdentityRole', newIdentity.type);
            } else {
                cachedIdentityRole.value = currentIdentityRole as Type;
            }
        },
        {immediate: true},
    );

    async function setUserIdentity(identityId: string) {
        try {
            if (selectedIdentity.value?.id !== identityId) {
                cookie.set('hopsi', identityId);
                await userEndpoints().selectIdentity({identityId});
                sessionStorage.removeItem('cachedIdentityRole');
                if (window.isEnforcedIdentity) {
                    window.location.href = '/dashboard';
                } else {
                    window.location.reload();
                }
            }
        } catch (e) {
            console.error(e);
        }
    }

    async function logoutUser() {
        await $fetch({url: '/logout', method: 'POST'});
        sessionStorage.clear();
        window.location.reload();
    }

    // - Languages

    const {availableLanguages, availableCountries} = useLanguages();

    const selectedLanguage = computed(
        () => availableLanguages.value.find((lang) => lang.name === user.value?.currentLanguage) ?? availableLanguages.value[0],
    );
    const selectedCountry = computed(
        () => availableCountries.value.find((lang) => lang.name === user.value?.currentCountry) ?? availableCountries.value[0],
    );

    // - Onboarding

    async function acknowledgeOnboarding(closeOnboarding = true) {
        if (closeOnboarding) {
            user.value!.options.onboarded = true;
        }
        await userEndpoints().acknowledgeOnboarding();
    }

    return {
        user,
        userIdentities,
        selectedIdentity,
        hasSelectedClientIdentity,
        hasSelectedFreelanceIdentity,
        cachedIdentityRole,
        setUserIdentity,
        logoutUser,
        selectedLanguage,
        selectedCountry,
        acknowledgeOnboarding,
    };
});
